import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandM,
  BrandS,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { TermsDialog } from '@sorare/core/src/components/conversionCredit/TermsDialog';
import { Dialog } from '@sorare/core/src/components/dialog';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ReferralLink } from '#src/components/missions/ThresholdsDuoTaskCampaign/ReferralLink/index.tsx';

import type { DuoTaskHowItWorksDialog_taskCampaign } from './__generated__/index.graphql.ts';

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;
const Content = styled(Vertical)`
  padding: var(--double-unit);
`;
const Title = styled(Horizontal).attrs({ center: true })`
  padding: var(--double-unit);
  background-color: var(--c-nd-100-opaque);
`;
const StyledLabelM = styled(BrandM)`
  padding: var(--unit);
  border-radius: 50%;
  background-color: var(--c-nd-100-opaque);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const List = styled(Vertical)`
  padding: var(--double-unit) 0;

  @media ${tabletAndAbove} {
    flex-direction: row;
    padding: var(--double-unit);
    align-items: flex-start;

    & > * {
      flex: 1;
    }
  }
`;
const StyledLink = styled(ButtonBase)`
  color: var(--c-nd-600);
  text-decoration: underline;
`;

export const DuoTaskHowItWorksDialog = ({
  campain,
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  campain: DuoTaskHowItWorksDialog_taskCampaign;
}) => {
  const isMobile = useIsMobile();
  const [openDialog, setOpenDialog] = useState(false);
  const { shareUrl, detailSections, headerMobileUrl, headerUrl, shareMessage } =
    campain;

  return (
    <Dialog open={open} onClose={onClose} hideHeader maxWidth="lg">
      {({ CloseButton }) => {
        return (
          <>
            <Vertical>
              <CloseButtonWrapper>
                <CloseButton className="close-button" onClose={onClose} />
              </CloseButtonWrapper>
              <img
                width="100%"
                src={isMobile ? headerMobileUrl : headerUrl}
                alt=""
              />
            </Vertical>
            <Title>
              <BrandM>
                <FormattedMessage {...glossary.howItWorks} />
              </BrandM>
              <Chip size="medium" color="yellow">
                <LabelM color="var(--c-yellow-400)" bold>
                  <Horizontal gap={0.5} as="span">
                    <FontAwesomeIcon icon={iconClock} />
                    <TimeLeft time={campain.endDate} />
                  </Horizontal>
                </LabelM>
              </Chip>
            </Title>
            <Content gap={5}>
              <List className="text-center" gap={4}>
                {detailSections.map((section, i) => (
                  <Vertical key={section.title} center>
                    <StyledLabelM bold>{i + 1}</StyledLabelM>
                    <BrandS>{section.title}</BrandS>
                    <BodyS color="var(--c-nd-600)">{section.description}</BodyS>
                  </Vertical>
                ))}
              </List>
              <Vertical gap={2} center>
                <ReferralLink
                  shareUrl={shareUrl || ''}
                  shareMessage={shareMessage || undefined}
                  buttonsSize="small"
                />
                <StyledLink onClick={() => setOpenDialog(true)} as={BodyS}>
                  *
                  <FormattedMessage defaultMessage="Referrals terms applied" />
                </StyledLink>
                {!!openDialog && (
                  <TermsDialog
                    terms="duoMissionTerms"
                    onClose={() => setOpenDialog(false)}
                  />
                )}
              </Vertical>
            </Content>
          </>
        );
      }}
    </Dialog>
  );
};

DuoTaskHowItWorksDialog.fragments = {
  taskCampaign: gql`
    fragment DuoTaskHowItWorksDialog_taskCampaign on TaskCampaign {
      slug
      shareUrl
      headerMobileUrl: headerUrl(mobile: true)
      headerUrl
      shareMessage
      endDate
      detailSections {
        title
        description
      }
    }
  ` as TypedDocumentNode<DuoTaskHowItWorksDialog_taskCampaign>,
};
